import React from "react";
import { Waves, Activity, Bed, Leaf, Apple, Sun, Bath } from "lucide-react";
import { RoutineBlock } from "@/components/SkinEssentialsRitual";
import SaveToPracticeButton from "@/components/SaveToPracticeButton";

/**
 * BodyCompanions — the whole-body and facial-care companion blocks that
 * live inside the Body pillar's Essentials tab.
 *
 * A `scope` prop lets Rituals render either the whole-body sections
 * (Movement / Nourishment / Sleep / Body Care), the facial sections
 * (Lymphatic Massage), or both. The organizing eyebrows (MOVE · NOURISH
 * · RESTORE · CARE) give the Body library the curated progression the
 * design brief asked for without duplicating existing content.
 */

const LYMPHATIC = [
  { title: "Warm the hands", subtitle: "Rub the palms together for a slow ten seconds until they feel warm.", purpose: ["Softens the pressure and signals the body to slow down before contact."] },
  { title: "Neck drainage", subtitle: "With flat fingers, sweep down the sides of the neck toward the collarbones. Ten passes each side.", purpose: ["Opens the primary drainage highway — everything else depends on it."] },
  { title: "Jawline sweep", subtitle: "From chin to ear, glide upward along the jaw with light knuckles. Then sweep down the neck.", purpose: ["Reduces puffiness and quiet tension held in the jaw."] },
  { title: "Under-eye press", subtitle: "With ring fingers, tap lightly from inner corner outward, then sweep down toward the ear.", purpose: ["Softens morning puffiness and supports fluid movement around the eyes."] },
  { title: "Décolletage & shoulders", subtitle: "Long strokes from the shoulder cap toward the collarbone. Then across the chest.", purpose: ["Clears the upper channels so the face has somewhere to drain into."] },
  { title: "Close with breath", subtitle: "Three slow breaths, hand resting over the heart.", purpose: ["Anchors the practice into the nervous system, not just the skin."] },
];

const MOVEMENT = [
  { title: "Morning walk in daylight", subtitle: "10–30 minutes outside within an hour of waking.", purpose: ["Anchors circadian rhythm and improves sleep the same night.", "Lifts mood through natural light and steady movement."] },
  { title: "Strength training", subtitle: "2–4 sessions per week. Compound lifts, progressive load.", purpose: ["Preserves lean muscle — the strongest lever for longevity.", "Supports bone density, blood sugar, and metabolic health."] },
  { title: "Zone 2 cardio", subtitle: "1–2 sessions per week, 30–60 minutes at a conversational pace.", purpose: ["Builds mitochondrial capacity and cardiovascular resilience.", "Low stress on the body, high return on longevity."] },
  { title: "Mobility & stretching", subtitle: "5–10 minutes daily. Hips, spine, shoulders, ankles.", purpose: ["Protects range of motion — the quiet currency of aging well.", "Reduces daily aches and lowers injury risk."] },
  { title: "Rest & recovery days", subtitle: "1–2 days per week that are intentionally low-effort.", purpose: ["Adaptation happens in recovery, not in the workout.", "Protects the nervous system from chronic overload."] },
];

const NOURISH = [
  { title: "Water before caffeine", subtitle: "A tall glass of water — with a pinch of salt if it feels right — before your first coffee.", purpose: ["Rehydrates before you stimulate. Small, quiet, cumulative."] },
  { title: "Protein at every meal", subtitle: "A palm-size portion of a protein you enjoy — eggs, fish, beans, yoghurt, tofu, lean meat.", purpose: ["Preserves lean tissue and steadies energy across the day."] },
  { title: "Plants at every meal", subtitle: "One or two colours of plants on the plate. Leafy, root, fruit, or a mix.", purpose: ["Fiber, minerals, and the small daily nutrients that add up over years."] },
  { title: "Eat without a screen", subtitle: "One meal a day, phone face-down. Notice the taste, the temperature, the pace.", purpose: ["Eating with attention improves digestion and satiety more than any supplement."] },
  { title: "Stop at pleasantly full", subtitle: "Aim for about 80% full at the end of a meal — the sweet spot for energy and metabolism.", purpose: ["A small daily practice that costs nothing and compounds gently."] },
];

const SLEEP = [
  { title: "Consistent bedtime and wake time", subtitle: "Same window every night, including weekends when possible.", purpose: ["The single most powerful sleep lever. Circadian rhythm loves rhythm."] },
  { title: "Light exposure, on purpose", subtitle: "Morning sunlight within an hour of waking. Dim, warm light after sunset.", purpose: ["Light is the primary cue that tells the body when to sleep and when to wake."] },
  { title: "A 60-minute wind-down", subtitle: "One hour before bed: no work, no bright screens, low light, slow tasks.", purpose: ["Sleep is a state you drift into — not a switch you flip. The body needs a runway."] },
  { title: "Cool, dark, quiet bedroom", subtitle: "65–68°F (18–20°C). Blackout or an eye mask. Silence or steady white noise.", purpose: ["Small environmental changes have outsized effects on sleep quality."] },
  { title: "Cut caffeine after noon", subtitle: "Caffeine has a 6–8 hour half-life — a 3 p.m. coffee is still working at 9 p.m.", purpose: ["Even when you fall asleep, caffeine reduces the deep, restorative stages."] },
  { title: "No screens 30–60 minutes before bed", subtitle: "Trade for a book, a walk, a bath, a slow conversation.", purpose: ["Blue light delays melatonin. Content activates the mind. Both cost you sleep quality."] },
];

const CARE = [
  { title: "Daily sun protection", subtitle: "SPF on exposed skin, every morning, all year round.", purpose: ["The single most powerful skin-longevity habit for the body and neck too — not just the face."] },
  { title: "Dry brushing before the shower", subtitle: "Two or three minutes with long strokes toward the heart.", purpose: ["Supports circulation, exfoliates gently, and quietly wakes the body."] },
  { title: "Moisturize on damp skin", subtitle: "Within a minute or two of the shower, while the skin is still slightly wet.", purpose: ["Traps water in the skin — a small change that changes texture over weeks."] },
  { title: "Contrast finish", subtitle: "End the shower with 20–60 seconds of cool water, gradually over time.", purpose: ["Boosts circulation, refreshes the nervous system, and steadies energy."] },
  { title: "Warm bath, on the hard days", subtitle: "Ten to fifteen minutes with epsom salts, once or twice a week if it helps.", purpose: ["Muscle recovery, ease for the joints, and a small ritual of being cared for."] },
];

export default function BodyCompanions({ scope = "all" }) {
  const showFace = scope === "all" || scope === "face";
  const showBody = scope === "all" || scope === "body";
  return (
    <div className={`space-y-14 ${scope === "all" ? "mt-14" : ""} reveal reveal-2`} data-testid={`body-companions-${scope}`}>

      {showBody && (
        <CategorySection Icon={Activity} label="Move" testid="body-cat-move">
          <SectionHeader
            eyebrow="Movement Essentials"
            title="The five that move the needle."
            body="You don't need a program — you need these five categories, in some form, most weeks. Everything else is a variation."
            saveMeta={{
              slug: "movement-essentials", title: "Movement Essentials", timeOfDay: "morning", durationMin: 30,
              description: "Five categories of movement — walking in daylight, strength training, zone-2 cardio, mobility, and recovery — practiced most weeks.",
              steps: MOVEMENT.map((s) => `${s.title}. ${s.subtitle}`),
            }}
            testid="body-movement-header"
            Icon={Activity}
          />
          <RoutineBlock icon={<Activity size={13} className="text-[hsl(var(--terracotta))]" />} eyebrow="The weekly shape" steps={MOVEMENT} testid="body-movement" />
        </CategorySection>
      )}

      {showBody && (
        <CategorySection Icon={Apple} label="Nourish" testid="body-cat-nourish">
          <SectionHeader
            eyebrow="Nourish Essentials"
            title="Eat like you belong to your life."
            body="Not a plan, not a program — five small choices that steady energy, protect muscle, and let food become a form of care."
            saveMeta={{
              slug: "nourish-essentials", title: "Nourish Essentials", timeOfDay: "anytime", durationMin: 15,
              description: "Five small choices — water before caffeine, protein at every meal, plants at every meal, one screen-free meal, stopping at pleasantly full.",
              steps: NOURISH.map((s) => `${s.title}. ${s.subtitle}`),
            }}
            testid="body-nourish-header"
            Icon={Apple}
          />
          <RoutineBlock icon={<Apple size={13} className="text-[hsl(var(--terracotta))]" />} eyebrow="The daily shape" steps={NOURISH} testid="body-nourish" />
        </CategorySection>
      )}

      {showBody && (
        <CategorySection Icon={Bed} label="Restore" testid="body-cat-restore">
          <SectionHeader
            eyebrow="Sleep Hygiene"
            title="Protect the hours that heal you."
            body="More recovery happens in the seven hours of sleep than in anything else you do all day. These are the environmental basics — quiet, unglamorous, and quietly transformative."
            saveMeta={{
              slug: "sleep-hygiene", title: "Sleep Hygiene", timeOfDay: "evening", durationMin: 60,
              description: "Six environmental basics that protect deep, restorative sleep — consistent hours, light exposure, wind-down, cool dark room, caffeine cutoff, no screens.",
              steps: SLEEP.map((s) => `${s.title}. ${s.subtitle}`),
            }}
            testid="body-sleep-header"
            Icon={Bed}
          />
          <RoutineBlock icon={<Bed size={13} className="text-[hsl(var(--terracotta))]" />} eyebrow="The nightly shape" steps={SLEEP} testid="body-sleep" />
          <div className="mt-6 rounded-md border hairline bg-[hsl(var(--sand))]/30 p-5" data-testid="body-sleep-closing">
            <div className="text-[10px] uppercase tracking-[0.24em] text-[hsl(var(--clay))] inline-flex items-center gap-2">
              <Leaf size={11} /> A quiet reminder
            </div>
            <p className="mt-2 text-sm text-[hsl(var(--fg))]/85 italic leading-relaxed">
              You cannot supplement your way out of a sleep debt. Every other choice compounds when this one is honored.
            </p>
          </div>
        </CategorySection>
      )}

      {showBody && (
        <CategorySection Icon={Sun} label="Care" testid="body-cat-care">
          <SectionHeader
            eyebrow="Body Care Essentials"
            title="Care that lives in the small daily moments."
            body="Not a routine to perform — a handful of quiet practices that support circulation, texture, and comfort in the body over the long horizon."
            saveMeta={{
              slug: "body-care-essentials", title: "Body Care Essentials", timeOfDay: "anytime", durationMin: 15,
              description: "Small daily practices — sun protection, dry brushing, moisturizing on damp skin, a contrast finish, a warm bath on the hard days.",
              steps: CARE.map((s) => `${s.title}. ${s.subtitle}`),
            }}
            testid="body-care-header"
            Icon={Bath}
          />
          <RoutineBlock icon={<Bath size={13} className="text-[hsl(var(--terracotta))]" />} eyebrow="The daily shape" steps={CARE} testid="body-care" />
        </CategorySection>
      )}

      {showFace && (
        <section data-testid="body-companions-lymphatic">
          <SectionHeader
            eyebrow="Lymphatic Massage Flow"
            title="Two minutes of drainage."
            body="A grounded sequence to move stagnation, ease puffiness, and give the face somewhere to breathe. Slow pressure, always downward toward the collarbones."
            saveMeta={{
              slug: "lymphatic-massage-flow", title: "Lymphatic Massage Flow", timeOfDay: "morning", durationMin: 3,
              description: "Two-minute face-and-neck drainage sequence — warm hands, neck drainage, jawline sweep, under-eye press, décolletage, close with breath.",
              steps: LYMPHATIC.map((s) => `${s.title}. ${s.subtitle}`),
            }}
            testid="body-lymphatic-header"
            Icon={Waves}
          />
          <RoutineBlock icon={<Waves size={13} className="text-[hsl(var(--terracotta))]" />} eyebrow="The sequence" steps={LYMPHATIC} testid="body-lymphatic" />
        </section>
      )}

    </div>
  );
}

/**
 * CategorySection — the MOVE / NOURISH / RESTORE / CARE editorial
 * eyebrow that anchors each Body sub-library.
 */
function CategorySection({ Icon, label, testid, children }) {
  return (
    <section data-testid={testid}>
      <div className="mb-6 flex items-center gap-3">
        <Icon size={12} className="text-[hsl(var(--copper))]" />
        <span className="text-[11px] uppercase tracking-[0.32em] text-[hsl(var(--copper))]">
          {label}
        </span>
        <span aria-hidden className="flex-1 h-px bg-[hsl(var(--line))]" />
      </div>
      {children}
    </section>
  );
}

/**
 * SectionHeader — a small editorial header with a Save-to-Your-Practice
 * button on the right so members can move an Essentials ritual into
 * their personal Practice list without leaving the page.
 */
function SectionHeader({ eyebrow, title, body, saveMeta, testid, Icon }) {
  return (
    <header className="mb-4 flex items-start justify-between gap-4 flex-wrap" data-testid={testid}>
      <div className="min-w-0">
        <div className="text-[11px] uppercase tracking-[0.24em] text-[hsl(var(--terracotta))] inline-flex items-center gap-2">
          {Icon && <Icon size={12} />} {eyebrow}
        </div>
        <h2 className="font-display text-3xl md:text-4xl mt-3 tracking-tight italic leading-tight">
          {title}
        </h2>
        <p className="mt-3 max-w-2xl text-sm text-[hsl(var(--muted-foreground))] leading-relaxed">
          {body}
        </p>
      </div>
      {saveMeta && (
        <SaveToPracticeButton
          slug={saveMeta.slug}
          title={saveMeta.title}
          description={saveMeta.description}
          steps={saveMeta.steps}
          timeOfDay={saveMeta.timeOfDay}
          durationMin={saveMeta.durationMin}
          testid={`save-to-practice-${saveMeta.slug}`}
        />
      )}
    </header>
  );
}
